"use client";
import { env } from "@/env";
import { TRPCProvider } from "@/lib/trpc";
import type { AppRouter } from "@ocr-monorepo/admin-api/router";
import {
  defaultShouldDehydrateQuery,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import {
  createTRPCClient,
  httpBatchLink,
  httpSubscriptionLink,
  splitLink,
} from "@trpc/client";
import posthog from "posthog-js";
import { useState } from "react";
import SuperJSON from "superjson";

const getBaseUrl = () => {
  if (typeof window !== "undefined") return window.location.origin;
  if (env.NEXT_PUBLIC_BASE_URL) return `https://${env.NEXT_PUBLIC_BASE_URL}`;
  return `http://localhost:${process.env.PORT ?? 3000}`;
};
function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
      },
      dehydrate: {
        serializeData: SuperJSON.serialize,
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) ||
          query.state.status === "pending",
      },
      hydrate: {
        deserializeData: SuperJSON.deserialize,
      },
    },
  });
}
let browserQueryClient: QueryClient | undefined = undefined;
function getQueryClient() {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

function getPostHogHeaders(): Record<string, string> {
  if (env.NODE_ENV === "development") return {};

  try {
    const distinctId = posthog.get_distinct_id();
    const sessionId = posthog.get_session_id();

    return {
      ...(distinctId ? { "x-posthog-distinct-id": distinctId } : {}),
      ...(sessionId ? { "x-posthog-session-id": sessionId } : {}),
    };
  } catch {
    return {};
  }
}

export default function TRPCClientProvider({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const queryClient = getQueryClient();
  const [trpcClient] = useState(() => {
    return createTRPCClient<AppRouter>({
      links: [
        splitLink({
          condition(op) {
            // Use subscription link for subscriptions, batch link for queries/mutations
            return op.type === "subscription";
          },
          true: httpSubscriptionLink({
            url: getBaseUrl() + "/api/trpc",
            transformer: SuperJSON,
            eventSourceOptions() {
              return {
                withCredentials: true,
              };
            },
          }),
          false: httpBatchLink({
            url: getBaseUrl() + "/api/trpc",
            transformer: SuperJSON,
            headers() {
              return {
                "x-trpc-source": "client",
                ...getPostHogHeaders(),
              };
            },
          }),
        }),
      ],
    });
  });
  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TRPCProvider>
    </QueryClientProvider>
  );
}
